import React, { useState, useEffect } from 'react';
import { ExternalLink, FileText, Phone, Globe, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { contentService } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const Resources = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { getText, isEnglish } = useLanguage();

  // Default resources - Chinese
  const defaultResources = [
    { id: '1', title: '香港法定假日', description: '2025年香港法定假日一覽', link: 'https://www.labour.gov.hk/tc/news/latest_holidays2025.htm' },
    { id: '2', title: '聘用家庭傭工的標準僱傭合約 (英文)', description: 'ID407 Standard Employment Contract (English)', link: 'https://www.fdh.labour.gov.hk/res/pdf/2017_SEC_Eng.pdf' },
    { id: '3', title: '聘用家庭傭工的標準僱傭合約 (中文)', description: 'ID407 標準僱傭合約 (中文版)', link: 'https://www.fdh.labour.gov.hk/res/pdf/2017_SEC_Chinese.pdf' },
    { id: '4', title: '終止僱傭合約通知書', description: 'ID407E 終止僱傭合約通知書', link: 'https://www.immd.gov.hk/pdforms/id407e.pdf' },
    { id: '5', title: '經修訂的住宿地址及家務安排', description: 'ID407G 經修訂的住宿地址及家務安排表格', link: 'https://www.immd.gov.hk/pdforms/id407g.pdf' },
    { id: '6', title: '家庭傭工簽證申請表格 (工人填寫)', description: 'ID988A 從外國受聘來港家庭傭工簽證／延長逗留期限申請表格', link: 'https://www.immd.gov.hk/pdforms/id988a.pdf' },
    { id: '7', title: '聘用家庭傭工申請表格 (僱主填寫)', description: 'ID988B 從外國聘用家庭傭工申請表格', link: 'https://www.immd.gov.hk/pdforms/ID988B.pdf' },
    { id: '8', title: '僱用外籍家庭傭工實用指南 (英文)', description: 'Practical Guide for Employment of Foreign Domestic Helpers', link: 'https://www.fdh.labour.gov.hk/res/pdf/FDHguideEnglish.pdf' },
    { id: '9', title: '僱用外籍家庭傭工實用指南 (中文)', description: '僱用外籍家庭傭工實用指南 (中文版)', link: 'https://www.fdh.labour.gov.hk/res/pdf/FDHguideChinese.pdf' },
    { id: '10', title: '僱用外籍家庭傭工實用指南 (印尼文)', description: 'Panduan Praktis Mempekerjakan Pekerja Rumah Tangga Asing', link: 'https://www.fdh.labour.gov.hk/res/pdf/FDHguideIndonesian.pdf' }
  ];

  // Default resources - English
  const defaultResourcesEn = [
    { id: '1', title: 'Hong Kong Public Holidays', description: '2025 Hong Kong Public Holidays List', link: 'https://www.labour.gov.hk/tc/news/latest_holidays2025.htm' },
    { id: '2', title: 'Standard Employment Contract (English)', description: 'ID407 Standard Employment Contract (English)', link: 'https://www.fdh.labour.gov.hk/res/pdf/2017_SEC_Eng.pdf' },
    { id: '3', title: 'Standard Employment Contract (Chinese)', description: 'ID407 Standard Employment Contract (Chinese)', link: 'https://www.fdh.labour.gov.hk/res/pdf/2017_SEC_Chinese.pdf' },
    { id: '4', title: 'Notice of Termination of Contract', description: 'ID407E Notice of Termination of Employment Contract', link: 'https://www.immd.gov.hk/pdforms/id407e.pdf' },
    { id: '5', title: 'Change of Address/Duties Form', description: 'ID407G Amended Accommodation Address and Domestic Duties', link: 'https://www.immd.gov.hk/pdforms/id407g.pdf' },
    { id: '6', title: 'Visa Application Form (Helper)', description: 'ID988A Foreign Domestic Helper Visa Application Form', link: 'https://www.immd.gov.hk/pdforms/id988a.pdf' },
    { id: '7', title: 'Employment Application Form (Employer)', description: 'ID988B Application for Employing Foreign Domestic Helper', link: 'https://www.immd.gov.hk/pdforms/ID988B.pdf' },
    { id: '8', title: 'Practical Guide for FDH (English)', description: 'Practical Guide for Employment of Foreign Domestic Helpers', link: 'https://www.fdh.labour.gov.hk/res/pdf/FDHguideEnglish.pdf' },
    { id: '9', title: 'Practical Guide for FDH (Chinese)', description: 'Practical Guide for Employment of Foreign Domestic Helpers (Chinese)', link: 'https://www.fdh.labour.gov.hk/res/pdf/FDHguideChinese.pdf' },
    { id: '10', title: 'Practical Guide for FDH (Indonesian)', description: 'Panduan Praktis Mempekerjakan Pekerja Rumah Tangga Asing', link: 'https://www.fdh.labour.gov.hk/res/pdf/FDHguideIndonesian.pdf' }
  ];

  // Default useful links - Chinese
  const defaultUsefulLinks = [
    { id: '1', title: '勞工處', description: '香港勞工處官方網站', link: 'https://www.labour.gov.hk/tc/index.htm' },
    { id: '2', title: '入境事務處', description: '香港入境事務處官方網站', link: 'https://www.immd.gov.hk/hkt/index.html' },
    { id: '3', title: '外籍家庭傭工專題網站', description: '勞工處外籍家庭傭工資訊', link: 'https://www.fdh.labour.gov.hk/tc/home.html' },
    { id: '4', title: '菲律賓駐港總領事館', description: 'Philippine Consulate General in Hong Kong', link: 'https://hongkongpcg.dfa.gov.ph/' },
    { id: '5', title: '印尼駐港總領事館', description: 'Consulate General of Indonesia in Hong Kong', link: 'https://kemlu.go.id/hongkong/en' }
  ];

  // Default useful links - English
  const defaultUsefulLinksEn = [
    { id: '1', title: 'Labour Department', description: 'Hong Kong Labour Department Official Website', link: 'https://www.labour.gov.hk/tc/index.htm' },
    { id: '2', title: 'Immigration Department', description: 'Hong Kong Immigration Department Official Website', link: 'https://www.immd.gov.hk/hkt/index.html' },
    { id: '3', title: 'FDH Portal', description: 'Labour Department Foreign Domestic Helper Portal', link: 'https://www.fdh.labour.gov.hk/tc/home.html' },
    { id: '4', title: 'Philippine Consulate', description: 'Philippine Consulate General in Hong Kong', link: 'https://hongkongpcg.dfa.gov.ph/' },
    { id: '5', title: 'Indonesian Consulate', description: 'Consulate General of Indonesia in Hong Kong', link: 'https://kemlu.go.id/hongkong/en' }
  ];

  const [resources, setResources] = useState(defaultResources);
  const [usefulLinks, setUsefulLinks] = useState(defaultUsefulLinks);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await contentService.get();
      setContent(response.data);
      if (response.data?.resources?.length > 0) {
        setResources(response.data.resources);
      }
      if (response.data?.usefulLinks?.length > 0) {
        setUsefulLinks(response.data.usefulLinks);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get display resources based on language
  const getDisplayResources = () => {
    if (!resources || resources.length === 0) {
      return isEnglish ? defaultResourcesEn : defaultResources;
    }
    
    if (isEnglish) {
      return resources.map((res, idx) => ({
        ...res,
        title: res.titleEn || defaultResourcesEn[idx]?.title || res.title,
        description: res.descriptionEn || defaultResourcesEn[idx]?.description || res.description
      }));
    }
    return resources;
  };
  
  const getDisplayUsefulLinks = () => {
    if (!usefulLinks || usefulLinks.length === 0) {
      return isEnglish ? defaultUsefulLinksEn : defaultUsefulLinks;
    }
    
    if (isEnglish) {
      return usefulLinks.map((link, idx) => ({
        ...link,
        title: link.titleEn || defaultUsefulLinksEn[idx]?.title || link.title,
        description: link.descriptionEn || defaultUsefulLinksEn[idx]?.description || link.description
      }));
    }
    return usefulLinks;
  };

  const displayResources = getDisplayResources();
  const displayUsefulLinks = getDisplayUsefulLinks();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#00b6a5' }} />
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 to-lime-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">
              <span style={{ color: '#00b6a5' }}>{getText('有用資料', 'Useful Resources')}</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {getText('常用表格、申請文件及相關政府網站連結', 'Common forms, application documents and related government website links')}
            </p>
          </div>
        </div>
      </section>

      {/* Forms and Documents Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8" style={{ color: '#00b6a5' }}>
            {getText('常用表格及文件', 'Common Forms and Documents')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayResources.map((resource) => (
              <Card key={resource.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div 
                      className="p-3 rounded-lg"
                      style={{ backgroundColor: '#f0fdf9' }}
                    >
                      <FileText className="h-6 w-6" style={{ color: '#00b6a5' }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {resource.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">
                        {resource.description}
                      </p>
                      <a
                        href={resource.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center text-sm font-medium hover:underline"
                        style={{ color: '#00b6a5' }}
                      >
                        {getText('查看 / 下載', 'View / Download')}
                        <ExternalLink className="ml-1 h-4 w-4" />
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Useful Links Section */}
      <section className="py-16" style={{ backgroundColor: '#f8fffe' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8" style={{ color: '#00b6a5' }}>
            {getText('常用網站連結', 'Useful Website Links')}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayUsefulLinks.map((link) => (
              <a
                key={link.id}
                href={link.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
              >
                <Card className="hover:shadow-lg transition-all hover:-translate-y-1 h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center space-x-4">
                      <div 
                        className="p-3 rounded-lg"
                        style={{ backgroundColor: '#f0fdf9' }}
                      >
                        <Globe className="h-6 w-6" style={{ color: '#00b6a5' }} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {link.title}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {link.description}
                        </p>
                      </div>
                      <ExternalLink className="h-5 w-5 text-gray-400 ml-auto" />
                    </div>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#00b6a5' }}>
            {getText('需要更多協助？', 'Need More Help?')}
          </h2>
          <p className="text-gray-600 mb-6">
            {getText('如有任何疑問，歡迎隨時聯絡我們', 'If you have any questions, feel free to contact us anytime')}
          </p>
          <a
            href={`https://api.whatsapp.com/send?phone=${(content?.whatsapp || '85261871388')}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              size="lg"
              className="text-lg px-8"
              style={{ backgroundColor: '#00b6a5' }}
            >
              <Phone className="mr-2 h-5 w-5" />
              {getText('WhatsApp 聯絡我們', 'WhatsApp Contact Us')}
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Resources;
